/**
 * Ficha do cliente do Gerenciador de Produção (redesign 29/10/2026): hero,
 * coluna de contexto, abas (Ambientes, Pendências, Observações, Linha do
 * tempo) e o painel do ambiente. Planilha para todos; Central (Analista,
 * pendências, conversa) só para quem tem `central_projetos:view`.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import AmbienteDetalhesDrawer from "../../../../components/AmbienteDetalhesDrawer";
import { usePrecosVisiveis } from "../../../../context/PrecosContext";
import { voltarParaALista } from "../comum/transicao";
import {
  datasChave,
  fatiasDosAmbientes,
  filtrarAmbientes,
  filtrosDeStatus,
  indicadoresDoHero,
  linhaDoTempo,
  pessoasDoCliente,
  proximaAcao,
} from "../dados/ficha";
import useCentralDaCarteira from "../dados/useCentralDaCarteira";
import useCentralDaFicha from "../dados/useCentralDaFicha";
import useComentariosDoCliente from "../dados/useComentariosDoCliente";
import { BarraDeAbas, PainelDaAba } from "./Abas";
import AbaAmbientes from "./AbaAmbientes";
import { LinhaDoTempo, ListaDePendencias, Observacoes, Trilho } from "./Acompanhamento";
import { BotaoRecolher, DatasChave, LeituraDoAnalista, PedidosEPessoas, ProximaAcao } from "./Contexto";
import Hero from "./Hero";
import NovaObservacao from "./NovaObservacao";

const CHAVE_RECOLHIDO = "pharuerp:clientes-legado:contexto-recolhido";
const ORDEM_DAS_ABAS = ["ambientes", "pendencias", "observacoes", "linha"];

function lerRecolhido() {
  try {
    return localStorage.getItem(CHAVE_RECOLHIDO) === "1";
  } catch {
    return false;
  }
}

function guardarRecolhido(valor) {
  try {
    localStorage.setItem(CHAVE_RECOLHIDO, valor ? "1" : "0");
  } catch {
    // Preferência cosmética: sem armazenamento, só não lembra na próxima visita.
  }
}

function rolarAte(elemento) {
  const reduzido = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  elemento?.scrollIntoView({ behavior: reduzido ? "auto" : "smooth", block: "start" });
}

/** Pedido visível na tela (para destacar na coluna de contexto). */
function usePedidoVisivel(pedidos) {
  const [ativo, setAtivo] = useState(pedidos[0]?.id || null);
  useEffect(() => {
    const alvos = pedidos.map((p) => document.getElementById(`pedido-${p.id}`)).filter(Boolean);
    if (!alvos.length || typeof IntersectionObserver === "undefined") return undefined;
    const obs = new IntersectionObserver((entradas) => {
      const visivel = entradas.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visivel) setAtivo(visivel.target.id.replace(/^pedido-/, ""));
    }, { rootMargin: "-120px 0px -50% 0px", threshold: [0, 0.25, 0.5] });
    alvos.forEach((a) => obs.observe(a));
    return () => obs.disconnect();
  }, [pedidos]);
  return [ativo, setAtivo];
}

export default function FichaDoCliente({ cliente }) {
  const navigate = useNavigate();
  const { precosVisiveis } = usePrecosVisiveis() || {};
  const carteira = useCentralDaCarteira();
  const central = useCentralDaFicha(cliente.numerosDosPedidos, carteira.pedidos);
  const comCentral = Boolean(central.ficha);
  const { comentarios, totalPorChave, recarregar: recarregarComentarios } = useComentariosDoCliente(cliente);

  const [aba, setAba] = useState("ambientes");
  const [direcao, setDirecao] = useState(1);
  const [recolhido, setRecolhido] = useState(lerRecolhido);
  const [busca, setBusca] = useState("");
  const [status, setStatus] = useState("");
  const [abertos, setAbertos] = useState(() => Object.fromEntries(cliente.pedidos.map((p) => [p.id, true])));
  const [ambiente, setAmbiente] = useState(null);
  const [observando, setObservando] = useState(false);
  const origemDoPainel = useRef(null);
  const campoDaBusca = useRef(null);
  const [pedidoAtivo, setPedidoAtivo] = usePedidoVisivel(cliente.pedidos);

  const indicadores = useMemo(() => indicadoresDoHero(cliente), [cliente]);
  const fatias = useMemo(() => fatiasDosAmbientes(cliente), [cliente]);
  const filtros = useMemo(() => filtrosDeStatus(cliente), [cliente]);
  const pessoas = useMemo(() => pessoasDoCliente(cliente, central.ficha?.cliente?.projetistas || []), [cliente, central.ficha]);
  const eventos = useMemo(() => linhaDoTempo(cliente, comentarios), [cliente, comentarios]);
  const pendencias = central.pendencias;
  const acao = useMemo(
    () => proximaAcao(cliente, { espera: central.ficha?.cliente?.quem_espera, pendencias: pendencias.length }),
    [cliente, central.ficha, pendencias.length],
  );
  const pedidosFiltrados = useMemo(
    () => cliente.pedidos.map((p) => ({ ...p, ambientesFiltrados: filtrarAmbientes(p.ambientes, { busca, status }) })),
    [cliente, busca, status],
  );

  const conversa = useMemo(() => {
    const lista = central.ficha?.conversas || [];
    return [...lista].sort((a, b) => Date.parse(b.ultima_mensagem_em || 0) - Date.parse(a.ultima_mensagem_em || 0))[0] || null;
  }, [central.ficha]);
  const semRespostaDesde = conversa?.ultima_mensagem && !conversa.ultima_mensagem.de_mim ? conversa.ultima_mensagem_em : null;

  const trocarAba = useCallback((nova) => {
    setDirecao(ORDEM_DAS_ABAS.indexOf(nova) >= ORDEM_DAS_ABAS.indexOf(aba) ? 1 : -1);
    setAba(nova);
  }, [aba]);

  function alternarRecolhido() {
    setRecolhido((atual) => {
      guardarRecolhido(!atual);
      return !atual;
    });
  }

  function agir(tipo) {
    if (tipo === "pendencias") {
      trocarAba("pendencias");
      return;
    }
    trocarAba("ambientes");
    if (tipo === "atraso") setStatus("atraso");
    if (tipo === "liberacao") setStatus(filtros.find((f) => f.id.startsWith("Aguardando libera"))?.id || "");
    setAbertos(Object.fromEntries(cliente.pedidos.map((p) => [p.id, true])));
    requestAnimationFrame(() => rolarAte(document.getElementById(`pedido-${cliente.pedidos[0]?.id}`)));
  }

  function irParaPedido(id) {
    trocarAba("ambientes");
    setAbertos((a) => ({ ...a, [id]: true }));
    setPedidoAtivo(id);
    requestAnimationFrame(() => rolarAte(document.getElementById(`pedido-${id}`)));
  }

  function abrirAmbiente(item, origem) {
    origemDoPainel.current = origem;
    setAmbiente(item);
  }

  const fecharAmbiente = useCallback(() => {
    setAmbiente(null);
    // Devolve o foco à linha de onde o painel abriu.
    requestAnimationFrame(() => origemDoPainel.current?.focus?.());
  }, []);

  const abas = [
    { id: "ambientes", rotulo: "Ambientes", total: cliente.totalAmbientes },
    { id: "pendencias", rotulo: "Pendências", total: pendencias.length, alerta: pendencias.length > 0, oculta: !comCentral, soEstreito: true },
    { id: "observacoes", rotulo: "Observações", total: cliente.observacoesDetalhadas.length + comentarios.length },
    { id: "linha", rotulo: "Linha do tempo", total: eventos.length, soEstreito: true },
  ];

  return (
    <div className="clg">
      <nav className="cl-crumb" aria-label="Caminho">
        <span>Gerenciador de Produção</span>
        <span aria-hidden="true">›</span>
        <a href="/legado/clientes" onClick={(e) => { e.preventDefault(); voltarParaALista(navigate); }}>Clientes</a>
        <span aria-hidden="true">›</span>
        <b>{cliente.cliente}</b>
      </nav>

      <Hero
        cliente={cliente}
        indicadores={indicadores}
        fatias={fatias}
        precosVisiveis={precosVisiveis}
        conversa={conversa}
        aoAbrirConversa={() => conversa && navigate(`/central-projetos/conversas/${conversa.id}`)}
        aoNovaObservacao={() => setObservando(true)}
        podeComentar
      />

      <div className="cl-lay" data-recolhido={recolhido}>
        <aside className="cl-aside cl-hscroll" aria-label="Contexto do cliente">
          <BotaoRecolher recolhido={recolhido} aoAlternar={alternarRecolhido} />
          <ProximaAcao acao={acao} aoAgir={agir} />
          {comCentral && (
            <LeituraDoAnalista central={{ cliente: central.ficha.cliente, semRespostaDesde }} />
          )}
          <DatasChave datas={datasChave(cliente)} />
          <PedidosEPessoas cliente={cliente} pessoas={pessoas} pedidoAtivo={pedidoAtivo} aoIrParaPedido={irParaPedido} />
        </aside>

        <section aria-label="Detalhes" style={{ minWidth: 0 }}>
          <BarraDeAbas abas={abas} ativa={aba} aoTrocar={trocarAba} />
          <PainelDaAba id={aba} direcao={direcao}>
            {aba === "ambientes" && (
              <AbaAmbientes
                pedidos={pedidosFiltrados}
                filtros={filtros}
                busca={busca}
                aoBuscar={setBusca}
                status={status}
                aoFiltrar={setStatus}
                abertos={abertos}
                aoAlternarPedido={(id) => setAbertos((a) => ({ ...a, [id]: !a[id] }))}
                ambienteAberto={ambiente?.id}
                comentariosDe={totalPorChave}
                precosVisiveis={precosVisiveis}
                aoAbrirAmbiente={abrirAmbiente}
                campoRef={campoDaBusca}
              />
            )}
            {aba === "pendencias" && (
              <ListaDePendencias pendencias={pendencias} podeTratar={central.podeTratar} aoMudar={central.recarregar} />
            )}
            {aba === "observacoes" && <Observacoes observacoes={cliente.observacoesDetalhadas} comentarios={comentarios} />}
            {aba === "linha" && <div className="cl-card"><LinhaDoTempo eventos={eventos} /></div>}
          </PainelDaAba>
        </section>

        <Trilho pendencias={pendencias} comCentral={comCentral} eventos={eventos}
          podeTratar={central.podeTratar} aoMudar={central.recarregar} />
      </div>

      <AmbienteDetalhesDrawer ambiente={ambiente} onClose={fecharAmbiente} />
      <NovaObservacao aberto={observando} cliente={cliente} aoFechar={() => setObservando(false)}
        aoSalvar={() => { setObservando(false); recarregarComentarios(); }} />
    </div>
  );
}
